import type { ApolloError } from '@apollo/client';
import type { ConstValues } from '@openx/types/helpers';
import React, { useContext } from 'react';

export type Publisher = {
  id: string;
  label: string;
};

export type AudienceInsightsContext = {
  publisherId?: string;
  publishers: Publisher[];
  isGeneratingReport: boolean;
  isReportGenerated: boolean;
  isReportError: boolean;
  isGenerateButtonDisabled: boolean;
  isDisabled: boolean;
  disabledMessage?: string;
  statistics: {
    totalMonthly: number;
    totalActiveUsers: number;
    totalActiveRampids: number;
    totalDesktop: number;
    totalMobileApp: number;
    totalCTV: number;
    totalTablets: number;
    totalRequestDisplay: number;
    totalRequestDisplayPercent: number;
    totalRequestVideo: number;
    totalRequestVideoPercent: number;
    totalRequestMobileApp: number;
    totalRequestMobileAppPercent: number;
    totalRequestMobileWeb: number;
    totalRequestMobileWebPercent: number;
    totalRequestDesktop: number;
    totalRequestDesktopPercent: number;
    totalRequestCTV: number;
    totalRequestCTVPercent: number;
    totalRequestIP: number;
    totalRequestIPPercent: number;
  };
  accountsLoading: boolean;
  accountsError?: ApolloError;
  changePublisher: (id: string) => void;
  generatePublisherReport: () => void;
  loading: boolean;
  error?: ApolloError;
};

export const ReportStatusValues = {
  COMPLETE: 'complete',
  PROCESSING: 'processing',
  ERROR: 'error',
  SF_COMPLETE: 'sf_account_complete',
  SF_PROCESSING: 'sf_account_processing',
  SF_ERROR: 'sf_account_error',
} as const;

export type ReportStatusValues = ConstValues<typeof ReportStatusValues>;

export const TOTAL_ID = '0';

export const audienceInsightsContext = React.createContext<AudienceInsightsContext>({
  publisherId: '',
  publishers: [],
  isGeneratingReport: false,
  isReportGenerated: false,
  isReportError: false,
  isGenerateButtonDisabled: false,
  isDisabled: false,
  disabledMessage: undefined,
  statistics: {
    totalMonthly: 0,
    totalActiveUsers: 0,
    totalActiveRampids: 0,
    totalDesktop: 0,
    totalMobileApp: 0,
    totalCTV: 0,
    totalTablets: 0,
    totalRequestDisplay: 0,
    totalRequestDisplayPercent: 0,
    totalRequestVideo: 0,
    totalRequestVideoPercent: 0,
    totalRequestMobileApp: 0,
    totalRequestMobileAppPercent: 0,
    totalRequestMobileWeb: 0,
    totalRequestMobileWebPercent: 0,
    totalRequestDesktop: 0,
    totalRequestDesktopPercent: 0,
    totalRequestCTV: 0,
    totalRequestCTVPercent: 0,
    totalRequestIP: 0,
    totalRequestIPPercent: 0,
  },
  accountsLoading: false,
  accountsError: undefined,
  changePublisher: () => null,
  generatePublisherReport: () => null,
  loading: false,
  error: undefined,
});

export function useAudienceInsights() {
  return useContext(audienceInsightsContext);
}
